import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash, IconEdit, IconFileInvoice, IconContract } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useCommitments,
  useDeleteCommitment,
  type Commitment,
} from "@/hooks/useCommitments";
import { CommitmentForm, type CommitmentFormData } from "@/components/CommitmentForm";
import { useCreateCommitment } from "@/hooks/useCommitments";

export const Route = createFileRoute("/projects/$projectId/commitments/" as any)({
  component: CommitmentsPage,
});

function CommitmentsPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const { data, isLoading } = useCommitments(projectId, {
    type: typeFilter as "po" | "subcontract" | undefined,
    status: statusFilter || undefined,
  });

  const createCommitment = useCreateCommitment(projectId);
  const deleteCommitment = useDeleteCommitment(projectId);

  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [commitmentToDelete, setCommitmentToDelete] = useState<Commitment | null>(null);

  const commitments = data?.commitments || [];
  const summary = data?.summary || {
    totalCommitments: 0,
    totalAmount: 0,
    byType: { po: 0, subcontract: 0 },
    byStatus: { draft: 0, issued: 0, received: 0, paid: 0 },
  };

  const handleAddCommitment = async (formData: CommitmentFormData) => {
    try {
      await createCommitment.mutateAsync({
        costCodeId: formData.costCodeId,
        vendorName: formData.vendorName,
        type: formData.type,
        amount: formData.amount,
        description: formData.description || undefined,
        date: formData.date,
        status: formData.status,
      });
      notifications.show({
        title: "Success",
        message: "Commitment created!",
        color: "green",
      });
      closeAddModal();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create commitment",
        color: "red",
      });
    }
  };

  const handleDeleteClick = (commitment: Commitment) => {
    setCommitmentToDelete(commitment);
    openDeleteModal();
  };

  const handleDeleteConfirm = async () => {
    if (!commitmentToDelete) return;

    try {
      await deleteCommitment.mutateAsync(commitmentToDelete.id);
      notifications.show({
        title: "Success",
        message: "Commitment deleted!",
        color: "green",
      });
      closeDeleteModal();
      setCommitmentToDelete(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete commitment",
        color: "red",
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "gray";
      case "issued":
        return "blue";
      case "received":
        return "orange";
      case "paid":
        return "green";
      default:
        return "gray";
    }
  };

  const getTypeIcon = (type: string) => {
    return type === "po" ? <IconFileInvoice size={16} /> : <IconContract size={16} />;
  };

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <Title order={2}>Commitments</Title>
            <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
              Add Commitment
            </Button>
          </Group>

          {/* Summary Cards */}
          <Group grow>
            <Paper p="md" withBorder style={{ background: "var(--mantine-color-blue-0)" }}>
              <Text size="sm" c="dimmed">
                Total Committed
              </Text>
              <Text size="xl" fw={700}>
                {formatCurrency(summary.totalAmount)}
              </Text>
              <Text size="xs" c="dimmed">
                {summary.totalCommitments} commitments
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Text size="sm" c="dimmed">
                Purchase Orders
              </Text>
              <Text size="lg" fw={600} c="blue">
                {formatCurrency(summary.byType.po)}
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Text size="sm" c="dimmed">
                Subcontracts
              </Text>
              <Text size="lg" fw={600} c="teal">
                {formatCurrency(summary.byType.subcontract)}
              </Text>
            </Paper>
          </Group>

          {/* Status Breakdown */}
          <Group gap="xs">
            <Badge variant="light" color="gray" size="sm">
              Draft: {summary.byStatus.draft}
            </Badge>
            <Badge variant="light" color="blue" size="sm">
              Issued: {summary.byStatus.issued}
            </Badge>
            <Badge variant="light" color="orange" size="sm">
              Received: {summary.byStatus.received}
            </Badge>
            <Badge variant="light" color="green" size="sm">
              Paid: {summary.byStatus.paid}
            </Badge>
          </Group>

          {/* Filters */}
          <Group>
            <Select
              placeholder="Filter by type"
              clearable
              value={typeFilter}
              onChange={setTypeFilter}
              data={[
                { value: "po", label: "Purchase Orders" },
                { value: "subcontract", label: "Subcontracts" },
              ]}
              style={{ width: 180 }}
            />
            <Select
              placeholder="Filter by status"
              clearable
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: "draft", label: "Draft" },
                { value: "issued", label: "Issued" },
                { value: "received", label: "Received" },
                { value: "paid", label: "Paid" },
              ]}
              style={{ width: 150 }}
            />
          </Group>

          {commitments.length === 0 ? (
            <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
              <Stack gap="md" align="center">
                <Text c="dimmed">No commitments yet.</Text>
                <Text size="sm" c="dimmed">
                  Track purchase orders and subcontracts to see your committed costs.
                </Text>
                <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
                  Add Your First Commitment
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Vendor</Table.Th>
                  <Table.Th>Cost Code</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: "100px" }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {commitments.map((commitment) => (
                  <Table.Tr key={commitment.id}>
                    <Table.Td>
                      <Group gap="xs">
                        {getTypeIcon(commitment.type)}
                        <Text size="sm" tt="capitalize">
                          {commitment.type === "po" ? "PO" : "Subcontract"}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {commitment.vendorName}
                      </Text>
                      {commitment.description && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {commitment.description}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{commitment.costCode}</Text>
                      <Text size="xs" c="dimmed">
                        {commitment.costCodeName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(commitment.date)}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="sm" fw={600}>
                        {formatCurrency(commitment.amount)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(commitment.status)} variant="light" size="sm">
                        {commitment.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() =>
                            navigate({
                              to: "/projects/$projectId/commitments/$commitmentId",
                              params: { projectId, commitmentId: commitment.id },
                            } as any)
                          }
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDeleteClick(commitment)}
                          loading={deleteCommitment.isPending}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      {/* Add Commitment Modal */}
      <Modal opened={addModalOpened} onClose={closeAddModal} title="Add Commitment" size="lg" centered>
        <CommitmentForm
          onSubmit={handleAddCommitment}
          onCancel={closeAddModal}
          loading={createCommitment.isPending}
          submitText="Create Commitment"
          projectId={projectId}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Delete Commitment" centered>
        <Stack gap="md">
          <Text>Are you sure you want to delete this commitment? This action cannot be undone.</Text>
          {commitmentToDelete && (
            <Paper p="sm" withBorder>
              <Text size="sm" fw={500}>
                {commitmentToDelete.vendorName}
              </Text>
              <Text size="sm" c="dimmed">
                {formatCurrency(commitmentToDelete.amount)} - {commitmentToDelete.type === "po" ? "Purchase Order" : "Subcontract"}
              </Text>
            </Paper>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteModal} disabled={deleteCommitment.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteConfirm} loading={deleteCommitment.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
